import React from 'react';

// Ebooks (multi-chapter volumes) live in their own nav section. They were
// previously rendered as a "Collections" table inside the Library, below the
// pieces — which made them appear to ignore the piece status filter (the filter
// only narrows pieces). Separating them keeps the Library + its status filter
// piece-only.

interface VolumeItem {
  slug: string;
  title: string;
  lifecycleState: string;
  chapterList: { slug: string; title?: string }[];
}

interface EbooksViewProps {
  volumes: VolumeItem[];
  onSelectVolume: (slug: string) => void;
  onNewVolume: () => void;
}

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  spark: { label: 'Idea', className: 'status-pill--idea' },
  research: { label: 'Idea', className: 'status-pill--idea' },
  plan: { label: 'Idea', className: 'status-pill--idea' },
  draft: { label: 'Writing', className: 'status-pill--writing' },
  review: { label: 'Review', className: 'status-pill--review' },
  ready: { label: 'Ready', className: 'status-pill--ready' },
  published: { label: 'Published', className: 'status-pill--live' },
  archived: { label: 'Archived', className: 'status-pill--archived' },
};

function humanTitle(slug: string): string {
  return slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function EbooksView({ volumes, onSelectVolume, onNewVolume }: EbooksViewProps) {
  if (volumes.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state__title">No ebooks yet</div>
        <div className="empty-state__text">Group related pieces into a multi-chapter ebook.</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onNewVolume}>+ New ebook</button>
      </div>
    );
  }
  return (
    <div className="library">
      <div className="library__toolbar">
        <div className="library__section-sep" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>Ebooks</span>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={onNewVolume}>+ New ebook</button>
        </div>
      </div>
      <table className="library__table">
        <tbody>
          {volumes.map((v) => {
            const status = STATUS_MAP[v.lifecycleState] || { label: v.lifecycleState, className: '' };
            return (
              <tr key={v.slug} className="library__row" tabIndex={0} onClick={() => onSelectVolume(v.slug)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectVolume(v.slug); } }}>
                <td className="library__td library__td--title">{v.title || humanTitle(v.slug)}</td>
                <td className="library__td"><span className={`status-pill ${status.className}`}>{status.label}</span></td>
                <td className="library__td library__td--dim">{v.chapterList?.length || 0} ch</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
